<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-4xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Report Reversal</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Report Carbon Reversal</h1>
            <p class="text-gray-600">
              If carbon sequestered by your project has been reversed (e.g. due to fire, disease, or other loss), you must report it. The registry will withdraw the appropriate number of credits from the buffer account to compensate.
            </p>
          </div>
        </section>

        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <p class="text-sm text-red-800">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Mandatory Reporting:</strong> Under Article 6.4, failure to report a reversal may result in project suspension. Report all reversals as soon as they are identified.
          </p>
        </div>

        <form class="space-y-6">
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-exclamation-circle text-error mr-3"></i>Reversal Details
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Affected Project *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select project</option>
                  <option>Mangrove Restoration Initiative – Borneo</option>
                  <option>Solar Park Beta – Northern Highlands</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Reversal Type *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select type</option>
                  <option>Wildfire</option>
                  <option>Disease / Pest</option>
                  <option>Flooding</option>
                  <option>Drought</option>
                  <option>Human Activity (unintentional)</option>
                  <option>Other Natural Cause</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Date of Reversal Event *</label>
                <input type="date" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Estimated tCO2e Reversed *</label>
                <input type="number" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="0" />
                <p class="text-xs text-gray-500 mt-1">Provide best estimate. A DOE will verify.</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Affected Area (hectares)</label>
                <input type="number" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="0" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Date Discovered *</label>
                <input type="date" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Description of Event *</label>
                <textarea rows="4" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Describe what happened, the extent of the loss, and any mitigation actions taken."></textarea>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-4 flex items-center">
              <i class="fas fa-upload text-primary mr-3"></i>Supporting Evidence
            </h2>
            <div class="space-y-4">
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="font-medium text-accent">Reversal Evidence Document *</h3>
                  <span class="text-xs text-error">Required</span>
                </div>
                <p class="text-xs text-gray-500 mb-3">Photos, satellite imagery, fire reports, or other evidence of the reversal event.</p>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-5 text-center hover:border-primary transition-colors cursor-pointer">
                  <i class="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-1"></i>
                  <p class="text-sm text-gray-600">Drag and drop or click to upload</p>
                  <p class="text-xs text-gray-500">PDF, JPG, PNG, ZIP up to 20MB</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Buffer account info -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-sm font-medium text-blue-800 mb-1">
              <i class="fas fa-shield-alt mr-2"></i>Buffer Account Withdrawal
            </p>
            <p class="text-sm text-blue-700">
              Upon verification of this reversal, the registry will withdraw the equivalent number of credits from your project's buffer account to compensate. If the buffer is insufficient, additional credits from your active holding may be required.
            </p>
          </div>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <label class="flex items-start space-x-3 cursor-pointer">
              <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
              <span class="text-sm text-gray-700">I declare this reversal report is accurate and complete. I understand that a DOE will be assigned to verify the extent of the reversal.</span>
            </label>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" class="px-8 py-3 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 flex items-center">
                <i class="fas fa-exclamation-triangle mr-2"></i>Submit Reversal Report
              </button>
            </div>
          </section>
        </form>

      </div>
    </main>
  </div>
</template>
